import React, { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import { Search, Download, Unlock, RotateCcw, Trash2, Power, ShieldOff, Activity } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(7);
  const [tl, setTl] = useState({ uid: null, data: null });

  const openTimeline = async (u) => {
    if (tl.uid === u.id) { setTl({ uid: null, data: null }); return; }
    setTl({ uid: u.id, data: null });
    try { const { data } = await api.get(`/admin/analytics/user/${u.id}`); setTl({ uid: u.id, data }); }
    catch { setTl({ uid: null, data: null }); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users", { params: { q } });
    setUsers(data.users);
  }, [q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setLimit(data.settings?.free_calc_limit ?? 7)).catch(() => {}); }, []);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error("Action failed"); } };

  const resetCalc = (u) => {
    const reason = window.prompt(`Reset free calculations for ${u.name}? Enter a reason:`, "");
    if (reason === null) return;
    act(() => api.post(`/admin/users/${u.id}/reset-calc`, { reason }), "Free calculations reset");
  };

  const exportUsers = async () => {
    try {
      const res = await api.get("/admin/users/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "users.xlsx"; a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold">Users</h1>
        <button onClick={exportUsers} className="btn-3d-green text-sm" data-testid="admin-export-users"><Download className="w-4 h-4" /> Export Excel</button>
      </div>

      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-thali-ink/40" />
        <input className="input-thali pl-10" placeholder="Search by name or mobile" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-user-search" />
      </div>

      {!users ? <Loader /> : (
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} className="card-thali" data-testid={`admin-user-${u.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading font-bold flex items-center gap-2">{u.name}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${u.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{u.active ? "Active" : "Inactive"}</span>
                  </div>
                  <div className="text-sm text-thali-ink/60">{u.mobile} · {u.entries_count} entries</div>
                  {u.profile?.school_name && <div className="text-xs text-thali-ink/50 mt-0.5">{u.profile.school_name}, {u.profile.block}, {u.profile.district}</div>}
                  <div className="text-xs mt-0.5">Recovery: {u.recovery_complete ? "✓ set" : "not set"}</div>
                  <div className="text-xs mt-0.5" data-testid={`admin-user-calc-${u.id}`}>Free calculations used: <span className={`font-bold font-num ${(u.free_calc_used ?? 0) >= limit ? "text-red-600" : "text-thali-green"}`}>{u.free_calc_used ?? 0}/{limit}</span></div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={() => act(() => api.put(`/admin/users/${u.id}`, { active: !u.active }), u.active ? "Deactivated" : "Activated")}
                  className="btn-ghost text-xs px-3 min-h-[40px]" data-testid={`admin-toggle-${u.id}`}>
                  <Power className="w-4 h-4" /> {u.active ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => resetCalc(u)} className="btn-ghost text-xs px-3 min-h-[40px]" data-testid={`admin-reset-calc-${u.id}`}><RotateCcw className="w-4 h-4" /> Reset Calc</button>
                <button onClick={() => act(() => api.post(`/admin/users/${u.id}/unlock`), "Unlocked")} className="btn-ghost text-xs px-3 min-h-[40px]" data-testid={`admin-unlock-${u.id}`}><Unlock className="w-4 h-4" /> Unlock</button>
                <button onClick={() => act(() => api.post(`/admin/users/${u.id}/reset-recovery`), "Recovery reset")} className="btn-ghost text-xs px-3 min-h-[40px]" data-testid={`admin-reset-${u.id}`}><ShieldOff className="w-4 h-4" /> Reset Recovery</button>
                <button onClick={() => openTimeline(u)} className="btn-ghost text-xs px-3 min-h-[40px]" data-testid={`admin-activity-${u.id}`}><Activity className="w-4 h-4" /> Activity</button>
                <button onClick={() => { if (window.confirm(`Delete ${u.name}?`)) act(() => api.delete(`/admin/users/${u.id}`), "Deleted"); }} className="btn-ghost text-xs px-3 min-h-[40px] text-red-600 border-red-200" data-testid={`admin-delete-${u.id}`}><Trash2 className="w-4 h-4" /> Delete</button>
              </div>
              {tl.uid === u.id && (
                <div className="mt-3 border-t border-thali-turmeric/20 pt-3" data-testid={`admin-timeline-${u.id}`}>
                  {!tl.data ? <div className="text-xs text-thali-ink/50">Loading activity…</div> : (
                    <>
                      <div className="text-xs text-thali-ink/60 mb-2">Last active: {tl.data.last_active ? new Date(tl.data.last_active).toLocaleString("en-IN") : "—"} · Device: {tl.data.device || "—"}</div>
                      {tl.data.timeline.length === 0 ? <div className="text-xs text-thali-ink/50">No activity recorded yet.</div> : (
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {tl.data.timeline.map((e, i) => {
                            const label = e.event === "bulk_save" ? `Bulk entries saved (${(e.props?.saved || 0) + (e.props?.updated || 0)} days)` : e.event;
                            const extra = e.event === "bulk_save" ? "" : (e.props?.type ? ` (${e.props.type})` : e.props?.fmt ? ` (${e.props.fmt})` : e.props?.days ? ` (${e.props.days})` : "");
                            return (
                              <div key={i} className="text-xs flex justify-between gap-2"><span className="font-medium">{label}{extra}</span><span className="text-thali-ink/50">{new Date(e.ts).toLocaleString("en-IN")}</span></div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
          {users.length === 0 && <div className="text-center py-8 text-thali-ink/50">No users found.</div>}
        </div>
      )}
    </div>
  );
}
